import { useEffect, useRef } from "react";
import { LanderConfigAction, useLanderConfig } from "../commons/ConfigContext";
import {
  sendAuctionExposureEvent,
  sendErrorEvent,
} from "../commons/LanderEvent";
import { ErrorCode } from "../commons/Constants";
import { useAuctionRedirectExperiment } from "./useAuctionRedirectExperiment";

// PARKING-5212 side effects for control, treatment_b, and treatment_c's URL fallback.
// Mounted at the parkweb layout entry points, so reaching here means every
// LanderRouter guard passed. A valid treatment_c redirect owns its exposure
// in AuctionDetailsRedirect and never mounts this layout.
export function useAuctionArmEffects(): void {
  const { landerConfig, landerConfigDispatch } = useLanderConfig();
  const { variant, ddUrl, ddUrlError } = useAuctionRedirectExperiment();

  const isOnLanderArm =
    variant === "control" ||
    variant === "treatmentB" ||
    (variant === "treatmentC" && ddUrlError);
  const inTreatmentCohort =
    variant === "treatmentB" || variant === "treatmentC";
  const hasDetailsUrlError = inTreatmentCohort && ddUrlError;
  const currentLink = landerConfig.lander?.banner?.link;

  // treatment_b: repoint the banner CTA at the Auctions DD page. The
  // link-equality guard keeps it idempotent against the reducer's deep-clone
  // re-run; the override lands post-effect, so first paint shows the SERP
  // link (valid fallback).
  useEffect(() => {
    if (variant !== "treatmentB" || !ddUrl || currentLink === ddUrl) return;
    landerConfigDispatch({
      type: LanderConfigAction.SET_BANNER_LINK,
      payload: { link: ddUrl },
    });
  }, [variant, ddUrl, currentLink, landerConfigDispatch]);

  // Channel: tag the adSense channel with the cohort so ad revenue is
  // attributable to the assigned arm, including treatment_c when it falls back.
  const channelUpdatedRef = useRef(false);
  useEffect(() => {
    if (!isOnLanderArm || channelUpdatedRef.current) return;
    channelUpdatedRef.current = true;
    landerConfigDispatch({
      type: LanderConfigAction.UPDATE_CHANNEL_BY_TREATMENT,
      payload: { inTreatmentCohort },
    });
  }, [isOnLanderArm, inTreatmentCohort, landerConfigDispatch]);

  // Exposure includes URL fallbacks without relabeling their assigned cohort.
  // The separate ERROR event distinguishes these from successful treatments.
  const exposedRef = useRef(false);
  useEffect(() => {
    if (!isOnLanderArm || exposedRef.current) return;
    exposedRef.current = true;
    sendAuctionExposureEvent(landerConfig);
  }, [isOnLanderArm, landerConfig]);

  const errorFiredRef = useRef(false);
  useEffect(() => {
    if (!hasDetailsUrlError || errorFiredRef.current) return;
    errorFiredRef.current = true;
    sendErrorEvent(landerConfig, ErrorCode.ERROR_AUCTION_DETAILS_URL);
  }, [hasDetailsUrlError, landerConfig]);
}
